import type { StatusBadgeVariant } from "@/components/shared/StatusBadge";

/**
 * Admin-facing status vocabulary for quote_request — shows the real internal
 * status, unlike the consumer masking in consumerQuoteRequestView.ts (which
 * hides e.g. 'triage_failed' behind a generic "triaging" label). Shared
 * between the admin list (quote-requests-triage-page.tsx) and detail
 * (platform-quote-request-detail-page.tsx) pages so both render the same
 * labels/colors without duplicating the mapping.
 */
export type AdminQuoteRequestStatus =
  | "draft"
  | "submitted"
  | "triaging"
  | "triaged"
  | "triage_failed"
  | "quoting"
  | "awarded"
  | "paid"
  | "fulfilling"
  | "completed"
  | "fulfillment_failed"
  | "expired"
  | "cancelled";

export const ADMIN_STATUS_FILTER_OPTIONS: { value: AdminQuoteRequestStatus | "all"; labelKey: string }[] = [
  { value: "all", labelKey: "platform.quoteRequestsTriage.statusAll" },
  { value: "submitted", labelKey: "platform.quoteRequestsTriage.statusSubmitted" },
  { value: "triaging", labelKey: "platform.quoteRequestsTriage.statusTriaging" },
  { value: "triaged", labelKey: "platform.quoteRequestsTriage.statusTriaged" },
  { value: "triage_failed", labelKey: "platform.quoteRequestsTriage.statusTriageFailed" },
  { value: "quoting", labelKey: "platform.quoteRequestsTriage.statusQuoting" },
  { value: "awarded", labelKey: "platform.quoteRequestsTriage.statusAwarded" },
  { value: "paid", labelKey: "platform.quoteRequestsTriage.statusPaid" },
  { value: "fulfilling", labelKey: "platform.quoteRequestsTriage.statusFulfilling" },
  { value: "completed", labelKey: "platform.quoteRequestsTriage.statusCompleted" },
  { value: "fulfillment_failed", labelKey: "platform.quoteRequestsTriage.statusFulfillmentFailed" },
  { value: "expired", labelKey: "platform.quoteRequestsTriage.statusExpired" },
  { value: "cancelled", labelKey: "platform.quoteRequestsTriage.statusCancelled" },
];

export const ADMIN_STATUS_VARIANTS: Record<AdminQuoteRequestStatus, StatusBadgeVariant> = {
  draft: "warning",
  submitted: "warning",
  triaging: "warning",
  triage_failed: "danger",
  triaged: "info",
  quoting: "info",
  awarded: "info",
  paid: "info",
  fulfilling: "info",
  completed: "success",
  fulfillment_failed: "danger",
  expired: "softDanger",
  cancelled: "softDanger",
};

export function adminStatusLabelKey(status: string): string {
  return (
    ADMIN_STATUS_FILTER_OPTIONS.find((o) => o.value === status)?.labelKey ??
    "platform.quoteRequestsTriage.statusAll"
  );
}
